import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';

import ModalContainer from '@components/modals/ModalContainer';
import LoaderComponent from '@components/LoaderComponent';
import RangePlanTable from '@components/rangePlan/RangePlanTable';
import { ENTITY_TYPE } from '@components/rangePlan/constants';

import CollectionPresentation from '@features/collection/components/CollectionPresentation';
import StyleDraftBoard from '@features/collection/details/StyleDraftBoard';
import AddNewFolder from '@features/favourites/components/AddNewFolder';
import FavouriteDetailsTopFilter from '@features/favourites/components/FavouriteDetailsTopFilter';
import FavouriteDetailsTopHeader from '@features/favourites/components/FavouriteDetailsTopHeader';
import ProductFavourite from '@features/favourites/components/ProductFavourite';
import SidePanel from '@features/favourites/components/SidePanel';
import { FAVOURITES_DETAILS_TABS } from '@features/favourites/utils/favouriteUtil';
import {
	allFilterPages,
	collectionDetailsFieldList,
	formatLocationOptions,
	formatMultiLayerFilteOptions,
	formatStyleTrakerOptions,
	getFilterParams
} from '@features/filter/static/filterUtil';
import FilterComponent from '@features/filter/v2/FilterComponent';
import FileUploads from '@features/blockSuggestion/components/fileUpload/FileUploads';

import { clearGlobalFilter, replaceDetailsPageFilterFields } from '@store/actions/FilterAction';
import { closeLoader, openLoader } from '@store/actions/LoaderAction';
import {
	fetchFavouriteProducts,
	fetchTotalLikeCount,
	setActiveFavouritePageTab
} from '@store/favourite/actions';
import { getFavouriteProducts } from '@store/reducers';

import { getProductDetailsFilterFields } from '@services/collectionService';
import { setSystemUserPreferences } from '@services/commonService';
import {
	getDraftedProducts,
	getFolderById,
	removeFromDraft,
	undraftProduct
} from '@services/favouriteService';

import { getUserPreference, printApiError } from '@utils/Util';

import { LIST_VIEW_TYPE } from '@/constants/constant';

import SampleComment from '@/components/SampleComment';
import CommonImage from '@/components/CommonImage';
import {
	useAddPersonalSettingMutation,
	useGetPersonalSettingsQuery,
	useArtboardProductTagMutation
} from '@/api/commonApis';
import BulkPresentationUploadModal from '@/features/favourites/components/BulkPresentationUploadModal';

const FavouriteDetails = () => {
	const userPreference = getUserPreference();
	const [isLoading, setIsLoading] = useState(false);
	const [isProductsLoading, setIsProductsLoading] = useState(false);
	const [page, setPage] = useState(0);
	const [designList, setDesignList] = useState({
		data: [],
		totalPages: 0,
		totalElements: 0,
		currentPage: 0
	});
	const { id } = useParams();

	let allFavouritesForProducts = useSelector(getFavouriteProducts);
	const [defaultViewType, setDefaultViewType] = useState(
		userPreference?.FAVOURITE_VIEW_TYPE || 'LARGE_VIEW'
	);

	const [selectedProducts, setSelectedProducts] = useState([]);
	const [selectedSortingOption, setSelectedSortingOption] = useState(
		userPreference?.FAVOURITE_PRODUCT_SORTING_OPTION || { code: 'NEWEST', title: 'Newest first' }
	);
	const [isSearchBoxHidden, setIsSearchBoxHidden] = useState(true);
	const [isShowSidePanel, setIsShowSidePanel] = useState(false);
	const [isShowDraft, setIsShowDraft] = useState(false);
	const [isEditFolder, setIsEditFolder] = useState(false);
	const [folderInfo, setFolderInfo] = useState(null);
	const [selectedFolderToEdit, setSelectedFolderToEdit] = useState(null);
	const [showComment, setShowComment] = useState(false);
	const dispatch = useDispatch();
	const [isPlayPresentation, setIsPlayPresentation] = useState(false);
	const [draftedProducts, setDraftedProducts] = useState([]);
	const activeTab = useSelector((store) => store.favourite?.favouritePageTab);
	const filterFields = useSelector((store) => store?.filterFieldStore);
	const { dependentFilterList, filterListPage } = useSelector((store) => store?.dependentFilterListStore);
	const [showFilter, setShowFilter] = useState(false);
	const location = useLocation();
	const [currentScrolledProduct, setCurrentScrolledProduct] = useState([]);
	const [artboardProducts, setArtboardProducts] = useState(null);
	const [isShowFileUploadModal, setIsShowFileUploadModal] = useState(false);
	const [isUploadedPresentation, setIsUploadedPresentation] = useState(false);
	const [uploadedProductIds, setUploadedProductIds] = useState([]);
	const [isPersonalSettingsApplied, setIsPersonalSettingsApplied] = useState(false);
	const pendingPersonalSettingsSaveRef = useRef(false);
	const lastPersonalSettingsSignatureRef = useRef('');

	const [addPersonalSetting] = useAddPersonalSettingMutation();
	const [artboardProductTag, { data: artboardProductTags }] = useArtboardProductTagMutation();

	const {
		data: personalSettings,
		isSuccess: personalSettingsSuccess,
		isError: personalSettingsError
	} = useGetPersonalSettingsQuery({
		settingKey: 'FOLDER_DETAILS_FILTER',
		params: { entityId: id, entityType: ENTITY_TYPE.FAVOURITE_FOLDER }
	});

	const sanitizeDetailsFiltersForPersonalSettings = (detailsFilters) => {
		if (!detailsFilters || typeof detailsFilters !== 'object') return detailsFilters;
		const cloned = { ...detailsFilters };
		delete cloned.referenceNumber;
		return cloned;
	};

	const collectOptionIds = (options = [], acc = new Set()) => {
		if (!Array.isArray(options)) return acc;
		for (const item of options) {
			if (!item || typeof item !== 'object') continue;
			if (item.id !== undefined && item.id !== null) acc.add(String(item.id));
			if (Array.isArray(item.subCategory)) collectOptionIds(item.subCategory, acc);
		}
		return acc;
	};

	const getValidKeysForDetailsFilter = (filterKey, dependentList) => {
		if (!dependentList || typeof dependentList !== 'object') return null;

		// Keys below are based on how Filter v2 maps API response -> option `id` (which becomes stored `key`).
		switch (filterKey) {
			case 'filterSubCategoryList': {
				const list = dependentList?.filterSubCategoryList;
				if (!Array.isArray(list)) return null;
				return new Set(list.map((i) => String(i?.constant)).filter(Boolean));
			}
			case 'lastStatusList': {
				const list = dependentList?.lastStatusList;
				if (!Array.isArray(list)) return null;
				return new Set(list.map((i) => String(i?.id)).filter(Boolean));
			}
			case 'marketIds': {
				const list = dependentList?.marketList;
				if (!Array.isArray(list)) return null;
				return new Set(list.map((i) => String(i?.id)).filter(Boolean));
			}
			case 'collectionIds': {
				const list = dependentList?.collectionList;
				if (!Array.isArray(list)) return null;
				return new Set(list.map((i) => String(i?.id)).filter(Boolean));
			}
			case 'tagIds': {
				const list = dependentList?.tagList || dependentList?.tagResponseList;
				if (!Array.isArray(list)) return null;
				return new Set(
					list
						.map((i) => String(i?.id ?? i?.text))
						.filter((v) => v !== 'undefined' && v !== 'null' && v !== '')
				);
			}
			case 'locationUpdateStatusList': {
				const list = dependentList?.locationUpdateStatusList;
				if (!Array.isArray(list)) return null;
				return new Set(list.map((i) => String(i?.constant)).filter(Boolean));
			}

			// Multi-layer filters (ids include synthetic `ALL` / `*-ALL` entries)
			case 'blockCategory': {
				const list = dependentList?.subCategoryList;
				if (!Array.isArray(list)) return null;
				const baseOptions = formatMultiLayerFilteOptions(list) || [];
				const grouped = (baseOptions || []).map((item) => {
					const allSubCategories = (item?.subCategory || []).flatMap((subCat) => {
						if (!subCat?.subCategory) return [];
						return subCat.subCategory.filter((sub) => !String(sub?.id).includes('ALL'));
					});
					const allCategoryEntry = {
						name: `All ${item?.name}`,
						id: `${item?.id}-ALL`,
						count: item?.count,
						label: 3,
						subCategory: [
							{
								name: 'All',
								id: `ALL`,
								count: item?.count,
								categoryId: item?.id,
								label: 4
							},
							...allSubCategories
						]
					};
					return {
						...item,
						subCategory: [
							allCategoryEntry,
							...(item?.subCategory || []).filter((sub) => !String(sub?.id).includes('ALL'))
						]
					};
				});
				return collectOptionIds(grouped);
			}
			case 'constructions': {
				const list = dependentList?.fabricTypeList;
				if (!Array.isArray(list)) return null;

				const options =
					list?.map((item) => {
						const { count, name, fabricConstructionGroupList, constant } = item || {};
						const validGroupList = Array.isArray(fabricConstructionGroupList)
							? fabricConstructionGroupList
							: [];

						const subCategoryList = validGroupList?.map((secondLayerItem) => {
							const constructionList = (secondLayerItem?.constructionList || []).map(
								(thirdLayerItem) => ({
									...thirdLayerItem,
									categoryId: secondLayerItem?.id,
									id: thirdLayerItem?.id,
									label: 4
								})
							);
							return {
								...secondLayerItem,
								id: secondLayerItem?.id,
								label: 3,
								subCategory: [
									{
										name: 'All',
										id: `ALL`,
										count: secondLayerItem?.count,
										categoryId: secondLayerItem?.id
									},
									...constructionList
								]
							};
						});

						const labelThreeCategories = [
							{ name: 'All', id: `ALL`, count, categoryId: constant },
							...validGroupList.flatMap(({ id: secondLayerId, constructionList }) =>
								(constructionList || []).map(({ id: thirdLayerId, ...thirdLayerProps }) => ({
									...thirdLayerProps,
									id: thirdLayerId,
									categoryId: secondLayerId,
									label: 4
								}))
							)
						];

						const labelThree = {
							name: `All ${name}`,
							id: `${constant}-ALL`,
							count,
							categoryId: constant,
							label: 3,
							subCategory: labelThreeCategories
						};

						return {
							count,
							name,
							id: constant,
							label: 2,
							subCategory: [labelThree, ...subCategoryList]
						};
					}) || [];

				return collectOptionIds(options);
			}
			case 'collectionLocationList': {
				const list = dependentList?.sampleInventoryLocationList;
				if (!Array.isArray(list)) return null;
				const options = formatLocationOptions(list) || [];
				return collectOptionIds(options);
			}
			case 'taskGroupList': {
				const list = dependentList?.taskGroupList;
				if (!Array.isArray(list)) return null;
				const options = formatStyleTrakerOptions(list) || [];
				return collectOptionIds(options);
			}

			// Team multi-layer stores filters under these keys (not under `teamList`)
			case 'CREATED_BY': {
				const list = dependentList?.createdByList;
				if (!Array.isArray(list)) return null;
				return new Set(list.map((i) => String(i?.id)).filter(Boolean));
			}
			case 'DESIGN_BY': {
				const list = dependentList?.designedByList;
				if (!Array.isArray(list)) return null;
				return new Set(list.map((i) => String(i?.id)).filter(Boolean));
			}
			case 'TP_COMPLETED_BY': {
				const list = dependentList?.tpCompletedByList;
				if (!Array.isArray(list)) return null;
				return new Set(list.map((i) => String(i?.id)).filter(Boolean));
			}
			case 'ADDED_TO_FAVOURITE_BY': {
				const list = dependentList?.addedToFavByList;
				if (!Array.isArray(list)) return null;
				return new Set(list.map((i) => String(i?.id)).filter(Boolean));
			}
			default:
				return null;
		}
	};

	const pruneInvalidDetailsFilters = (detailsFilters, dependentList) => {
		if (!detailsFilters || typeof detailsFilters !== 'object') return detailsFilters;

		const pruned = {};
		for (const [filterKey, selected] of Object.entries(detailsFilters)) {
			if (!Array.isArray(selected)) continue;

			const validKeys = getValidKeysForDetailsFilter(filterKey, dependentList);

			// If we don't have options for this filterKey, skip persisting it to avoid garbage.
			if (!validKeys) continue;

			const cleaned = selected.filter((item) => validKeys.has(String(item?.key)));
			if (cleaned.length > 0) pruned[filterKey] = cleaned;
		}

		return pruned;
	};

	useEffect(() => {
		setArtboardProducts((prev) => ({
			...prev,
			...artboardProductTags
		}));
	}, [page, artboardProductTags]);

	useEffect(() => {
		if (
			personalSettings?.value &&
			parseInt(personalSettings?.entityId) === parseInt(id) &&
			personalSettings?.entityType === ENTITY_TYPE.FAVOURITE_FOLDER
		) {
			dispatch(replaceDetailsPageFilterFields(JSON.parse(personalSettings?.value)));
			setIsPersonalSettingsApplied(true);
		}
	}, [personalSettings?.value]);

	useEffect(() => {
		// Reset for a new folder id so we don't fetch with stale "applied" state.
		setIsPersonalSettingsApplied(false);
	}, [id]);

	useEffect(() => {
		// If settings fetch finished but there's nothing to apply (or it errored), we can proceed with defaults.
		if (!id) return;
		if (!(personalSettingsSuccess || personalSettingsError)) return;
		if (!personalSettings?.value) {
			setIsPersonalSettingsApplied(true);
		}
	}, [id, personalSettingsSuccess, personalSettingsError, personalSettings?.value]);

	const SIZE = 30;

	useEffect(() => {
		if (isFilterFromSidePanel()) return;
		// Wait for personal settings query to resolve, and (if present) for saved filters to be applied.
		if (!id) return;
		if (!(personalSettingsSuccess || personalSettingsError)) return;
		if (personalSettings?.value && !isPersonalSettingsApplied) return;

		{
			setIsProductsLoading(true);
			dispatch(
				fetchFavouriteProducts(
					0,
					SIZE,
					'',
					selectedSortingOption?.code,
					id,
					false,
					createFilterParams(),
					defaultViewType === LIST_VIEW_TYPE.LIST_VIEW,
					setCurrentScrolledProduct
				)
			).finally(() => {
				setIsProductsLoading(false);
			});
			setPage(0);
		}
	}, [
		selectedSortingOption?.code,
		activeTab,
		defaultViewType,
		filterFields,
		id,
		personalSettingsSuccess,
		personalSettingsError,
		personalSettings?.value,
		isPersonalSettingsApplied
	]);

	useEffect(() => {
		if (currentScrolledProduct?.length > 0) {
			artboardProductTag(currentScrolledProduct);
		}
	}, [currentScrolledProduct?.length]);

	// 1) Fetch dependent filter options whenever filter changes while filter bar is open.
	useEffect(() => {
		if (!showFilter) return;

		const { filterParams } = getFilterParams(getFilterFieldFromStore(), id, 'folderId', true);
		dispatch(getProductDetailsFilterFields({ ...filterParams }));

		// Mark that we should save once options are available for validation.
		const detailsFiltersForSave = sanitizeDetailsFiltersForPersonalSettings(
			filterFields?.DETAILS_PAGE_FILTERS || {}
		);
		const signature = JSON.stringify(detailsFiltersForSave || {});
		if (signature !== lastPersonalSettingsSignatureRef.current) {
			lastPersonalSettingsSignatureRef.current = signature;
			pendingPersonalSettingsSaveRef.current = Object.keys(detailsFiltersForSave || {}).length > 0;
		}
	}, [filterFields, showFilter, id]);

	// 2) Save personal settings only after dependent options arrive (prevents infinite fetch loop).
	useEffect(() => {
		if (!showFilter) return;
		if (!pendingPersonalSettingsSaveRef.current) return;

		const isFavouriteDetailsDependentListReady =
			filterListPage === allFilterPages.FAVOURITE_DETAILS && !!dependentFilterList;
		if (!isFavouriteDetailsDependentListReady) return;

		const detailsFiltersForSave = sanitizeDetailsFiltersForPersonalSettings(
			filterFields?.DETAILS_PAGE_FILTERS || {}
		);
		const cleanedDetailsFiltersForSave = pruneInvalidDetailsFilters(
			detailsFiltersForSave,
			dependentFilterList
		);

		if (Object.keys(cleanedDetailsFiltersForSave || {}).length > 0) {
			addPersonalSetting({
				body: {
					key: 'FOLDER_DETAILS_FILTER',
					entityId: id,
					entityType: ENTITY_TYPE.FAVOURITE_FOLDER,
					value: JSON.stringify({
						...(filterFields || {}),
						DETAILS_PAGE_FILTERS: cleanedDetailsFiltersForSave
					})
				}
			});
		}

		pendingPersonalSettingsSaveRef.current = false;
	}, [dependentFilterList, filterListPage, showFilter, id]);

	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const isShowComment = params.get('showComment');
		if (isShowComment) {
			setShowComment(true);
		}
	}, [id]);

	useEffect(() => {
		// Clear global filter when component unmounts
		return () => {
			dispatch(clearGlobalFilter());
		};
	}, []);

	const getFilterFieldFromStore = () => {
		return parseInt(id) === -1 ? filterFields : filterFields?.DETAILS_PAGE_FILTERS;
	};

	const createFilterParams = () => {
		const { body } = getFilterParams(getFilterFieldFromStore(), id, '', true);
		return body;
	};

	useEffect(() => {
		if (designList?.data?.length) {
			dispatch(fetchTotalLikeCount());
		}
	}, [designList?.data?.length]);

	const isFilterFromSidePanel = () => {
		const referenceNumber = filterFields?.DETAILS_PAGE_FILTERS?.referenceNumber;
		if (!referenceNumber?.length) return false;
		return referenceNumber[0]?.isAsyncSearch === true;
	};

	const fetchDraftedProducts = async () => {
		try {
			setIsLoading(true);
			const response = await getDraftedProducts({
				page: 0,
				size: 1000,
				sortingOption: selectedSortingOption?.code,
				search: '',
				folderId: id,
				isDrafted: true,
				isListView: false
			});

			const draftedData =
				response?.data?.map((item) => ({
					...item?.productResponse
				})) || [];

			setDraftedProducts(draftedData);
		} catch (error) {
			printApiError(error);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		fetchDraftedProducts();
	}, [isShowDraft]);

	const handleDefaultViewType = async (viewType) => {
		const body = {
			...userPreference,
			FAVOURITE_VIEW_TYPE: viewType
		};
		setDefaultViewType(viewType);
		await setSystemUserPreferences(body);
	};

	const onChangeAllCheckAndSearchForStyle = async (e) => {
		let { name, value, checked } = e.target;
		if (name === 'allCheckBoxForStyle') {
			setSelectedProducts(checked ? designList?.data?.map((item) => item.id) : []);
		} else if (name === 'searchForStyle') {
			setIsLoading(true);
			setIsProductsLoading(true);
			dispatch(
				fetchFavouriteProducts(
					0,
					SIZE,
					value,
					selectedSortingOption?.code,
					id,
					false,
					createFilterParams(),
					defaultViewType === LIST_VIEW_TYPE.LIST_VIEW
				)
			).finally(() => {
				setIsLoading(false);
				setIsProductsLoading(false);
			});
		}
	};

	useEffect(() => {
		if (id) {
			fetchFolderById();
		}
	}, [id]);

	const fetchFolderById = async () => {
		try {
			setIsLoading(true);
			const response = await getFolderById(`?folderId=${id}`);
			setFolderInfo(response);
		} catch (error) {
			printApiError(error);
		} finally {
			setIsLoading(false);
		}
	};

	const handleEditFolder = (folder) => {
		setIsEditFolder(true);
		setSelectedFolderToEdit(folder);
	};

	const handleCloseModal = () => {
		setIsShowSidePanel(false);
		setIsEditFolder(false);
		setSelectedFolderToEdit(null);
	};

	const handleUndraftProduct = async (design) => {
		try {
			dispatch(openLoader());
			await removeFromDraft(design?.id, id);
			fetchDraftedProducts();
			dispatch(
				fetchFavouriteProducts(
					0,
					SIZE,
					'',
					selectedSortingOption?.code,
					id,
					false,
					[],
					defaultViewType === LIST_VIEW_TYPE.LIST_VIEW
				)
			);
		} catch (error) {
			printApiError(error);
		} finally {
			dispatch(closeLoader());
		}
	};

	const handleUnLikeProduct = async (productId) => {
		try {
			dispatch(openLoader());
			await undraftProduct(productId, id);
			fetchDraftedProducts();
			dispatch(fetchTotalLikeCount());
		} catch (error) {
			printApiError(error);
		} finally {
			dispatch(closeLoader());
		}

		setDraftedProducts(draftedProducts.filter((item) => parseInt(item?.id) !== parseInt(productId)));
	};

	const handleSortingOptionChange = async (option) => {
		setSelectedSortingOption(option);
		const body = {
			FAVOURITE_PRODUCT_SORTING_OPTION: option
		};
		await setSystemUserPreferences(body);
	};

	const isSoldFolder = () => folderInfo?.name === 'Sold Interest';
	const filteredTabs = isSoldFolder()
		? FAVOURITES_DETAILS_TABS.filter((tab) => tab.code !== 'RANGE_PLAN' && tab.code !== 'FILES')
		: FAVOURITES_DETAILS_TABS;

	return (
		<>
			<FavouriteDetailsTopHeader onEditFolder={handleEditFolder} folderInfo={folderInfo} folderId={id}>
				<div className="navigation-tab shadow-nav-4dp bg-white">
					<ul>
						{filteredTabs.map((item, index) => (
							<li
								key={`${item.code}-${index}`}
								className={`${activeTab.code === item.code ? 'active' : ''}`}
								onClick={() => dispatch(setActiveFavouritePageTab(item))}
							>
								{item.title}
							</li>
						))}
					</ul>
				</div>
				<div>
					{activeTab.code !== 'RANGE_PLAN' && activeTab.code !== 'FILES' && (
						<>
							<FavouriteDetailsTopFilter
								allFavouritesForProducts={allFavouritesForProducts}
								isSearchBoxHidden={isSearchBoxHidden}
								setIsSearchBoxHidden={setIsSearchBoxHidden}
								onChangeAllCheckAndSearchForStyle={onChangeAllCheckAndSearchForStyle}
								defaultViewType={defaultViewType}
								handleDefaultViewType={handleDefaultViewType}
								setIsPlayPresentation={setIsPlayPresentation}
								onSelectOption={handleSortingOptionChange}
								setPage={setPage}
								selectedSortingOption={selectedSortingOption}
								setIsShowSidePanel={setIsShowSidePanel}
								setIsShowDraft={setIsShowDraft}
								draftedProducts={draftedProducts}
								id={id}
								designList={designList}
								getCategoryIdList={createFilterParams}
								setShowFilter={setShowFilter}
								setIsShowFileUploadModal={setIsShowFileUploadModal}
								folderInfo={folderInfo}
								handleSuccess={fetchFolderById}
								isSoldFolder={isSoldFolder()}
							/>
						</>
					)}
				</div>
				{isShowSidePanel && (
					<SidePanel
						setIsShowSidePanel={setIsShowSidePanel}
						selectedSortingOption={selectedSortingOption}
						fetchFavouriteProducts={fetchFavouriteProducts}
						viewType={defaultViewType}
						setProductList={setDesignList}
						productList={designList}
						handleSortingOptionChange={handleSortingOptionChange}
					/>
				)}
				{isShowFileUploadModal && (
					<BulkPresentationUploadModal
						onClose={() => {
							setIsShowFileUploadModal(false);
							setIsUploadedPresentation(false);
							setUploadedProductIds([]);
						}}
						setIsUploadedPresentation={setIsUploadedPresentation}
						setUploadedProductIds={setUploadedProductIds}
						folderId={id}
					/>
				)}
			</FavouriteDetailsTopHeader>
			<section
				className={`${activeTab.code === 'FILES' ? 'bg-gray' : 'bg-white'} filter-data-container filter-v3-data-container py-0`}
			>
				{activeTab.code === 'DESIGN' && (
					<FilterComponent
						filterPageName={allFilterPages.FAVOURITE_DETAILS}
						fields={collectionDetailsFieldList}
						showFilter={showFilter}
						isDependentFilter={true}
						className="favourite-details-filter-bar"
					/>
				)}
				{activeTab.code === 'DESIGN' && (
					<div className="data-table">
						{folderInfo?.description !== undefined && (
							<div className="description-container mb-2">
								<p className="regular-14 gray_dark_02">{folderInfo?.description}</p>
							</div>
						)}
						<LoaderComponent
							loading={
								(!!id && !(personalSettingsSuccess || personalSettingsError)) ||
								isProductsLoading
							}
						>
							<ProductFavourite
								isLoading={isLoading}
								setIsLoading={setIsLoading}
								designList={designList}
								setDesignList={setDesignList}
								selectedProducts={selectedProducts}
								setSelectedProducts={setSelectedProducts}
								defaultViewType={defaultViewType}
								allFavourites={allFavouritesForProducts}
								onChangeAllCheck={onChangeAllCheckAndSearchForStyle}
								selectedSortingOption={selectedSortingOption}
								page={page}
								setPage={setPage}
								folderId={id}
								folderInfo={folderInfo}
								fetchDraftedProducts={fetchDraftedProducts}
								getCategoryIdList={createFilterParams}
								brandId={folderInfo?.brandResponse?.id}
								createFilterParams={createFilterParams}
								artboardProducts={artboardProducts}
								setCurrentScrolledProduct={setCurrentScrolledProduct}
							/>
						</LoaderComponent>
					</div>
				)}
				{activeTab.code === 'RANGE_PLAN' && (
					<div className="data-table mw-100">
						<RangePlanTable
							entityType={ENTITY_TYPE.FAVOURITE_FOLDER}
							brandName={folderInfo?.brandResponse?.name}
							brandId={folderInfo?.brandResponse?.id}
						/>
					</div>
				)}
				{activeTab.code === 'FILES' && (
					<div className="collection-file-section bg-white mx-auto p-5 mt-4">
						<FileUploads
							id={id}
							originFrom="FILE"
							onSetActiveTool={() => false}
							onPlaceImageOnArtboard={() => false}
							entityType="FAVOURITE_FOLDER"
							allowedFileType="*"
						/>
					</div>
				)}

				{id > 0 && (
					<div className="conversation-btn">
						<button
							className="button text absolute-conversation-btn"
							onClick={() => {
								setShowComment(true);
								setFolderInfo((prev) => ({
									...prev,
									noOfUnreadMessage: 0
								}));
							}}
						>
							<CommonImage src={`/icons/comments-gray.svg`} alt="not read" />
						</button>
					</div>
				)}
			</section>
			<div className="collection-details-container">
				{isShowDraft && (
					<StyleDraftBoard
						onClose={() => setIsShowDraft(false)}
						totalDraft={draftedProducts?.length}
						unDraftProduct={handleUndraftProduct}
						products={draftedProducts}
						parentComponent="FAVOURITE_DETAILS"
						onUnLike={handleUnLikeProduct}
					/>
				)}
			</div>

			{isEditFolder && (
				<ModalContainer className="sample-location-modal">
					<AddNewFolder
						onCloseModal={() => handleCloseModal()}
						isEdit={isEditFolder}
						selectedFolderToEdit={selectedFolderToEdit}
						handleSuccess={fetchFolderById}
					/>
				</ModalContainer>
			)}

			<CollectionPresentation
				isPlay={isPlayPresentation || isUploadedPresentation}
				collectionName={folderInfo?.name}
				collectionId={id}
				onCLoseModal={() => {
					setIsPlayPresentation(false);
					setIsUploadedPresentation(false);
				}}
				entityType="FAVOURITE_FOLDER"
				selectedProductIds={isUploadedPresentation ? uploadedProductIds : selectedProducts}
				isShowPSD={isUploadedPresentation}
			/>

			<SampleComment
				sampleRequestId={id}
				showComment={showComment}
				setShowComment={() => setShowComment(false)}
				brandInfo={{
					brandId: folderInfo?.brandResponse?.id,
					brandName: folderInfo?.brandResponse?.name
				}}
				postTypeFilter={'FAVOURITE_FOLDER_COMMENT'}
				getPostUrl={'getFavouriteConversation'}
				addPostUrl={'addNewConversationPost'}
				postTypeId={'favouriteFolderId'}
				topic={'FOLDER_CONVERSATION'}
				styleList={designList?.data}
			/>
		</>
	);
};

export default FavouriteDetails;
